import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openUrlInNewTab } from "../../../utils/helper";
import { get } from "lodash";
import { CircularProgress } from "@material-ui/core";
import companyPublicView from "../../../services/companyPublicView.services";
import { companyJobsList } from "../../../reducers/companyPublicView/companyPublicView.reducer";
import appRoutes from "../../../routes/app.routes";
import { Skeleton } from "@material-ui/lab";
import NotFoundPage from "../../../components/common/NotFoundPage";
import Avatar from "../../../components/common/AvtarImage";
import { formatDate } from "../../../utils/helper";

const _ = { get };

const htmlToPreviewText = (html: string): string => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
};

type JobsProps = {
  loading?: boolean;
};

const Jobs = ({ loading: profileLoading = false }: JobsProps) => {
  const dispatch = useDispatch();
  const { companyPublicProfile } = useSelector(
    ({ companyPublicView }: any) => companyPublicView.companyPublicView
  );
  const [jobs, setJobs] = useState<any>([]);
  const [btnLoading, setBtnLoading] = useState<boolean>(false);
  const [initialJobsLoading, setInitialJobsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState<number>(0);
  const pageSize = 20;
  const [count, setCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);

  const jobsCard = (row) => {
    return (
      <div
        onClick={() => {
          openUrlInNewTab(
            appRoutes.jobDetail.generatePath(_.get(row, "slug", ""))
          );
        }}
        className="job-item"
      >
        <div className="job-item-inner">
          <div className="job-item-header">
            <div className="avatar">
              <Avatar
                src={_.get(
                  companyPublicProfile,
                  "companyProfile.company_logo",
                  ""
                )}
                size="xsm"
                type="job"
              />
            </div>
            <div className="meta">
              <h4>{`${_.get(row, "title", "")}`}</h4>
              <div>
                <p className="meta-location">{_.get(row, "location", "")}</p>
                <p className="meta-company">{_.get(row, "company_name", "")}</p>
              </div>
            </div>
          </div>
          <div className="job-item-content">
            {htmlToPreviewText(_.get(row, "description", ""))}
          </div>
          <div className="job-item-footer">
            <p>Posted On: {formatDate(_.get(row, "updated_at", ""))}</p>
          </div>
        </div>
      </div>
    );
  };

  const fetchMore = useCallback(async () => {
    const companyId = companyPublicProfile?.id;
    if (!companyId || btnLoading || count >= totalCount) return;

    setBtnLoading(true);
    try {
      const res: any = await companyPublicView.getCompanyJobs({
        companyId,
        pageSize,
        page,
      });
      const jobsArray =
        res?.flag === false
          ? []
          : Array.isArray(res?.data)
            ? res.data
            : [];
      const merged = [...jobs, ...jobsArray];
      setJobs(merged);
      setCount(merged.length);
      dispatch(companyJobsList(merged));
      setPage((p) => p + 1);
      if (res?.pagination?.totalRecords != null) {
        setTotalCount(res.pagination.totalRecords);
      }
    } finally {
      setBtnLoading(false);
    }
  }, [
    companyPublicProfile?.id,
    btnLoading,
    count,
    totalCount,
    page,
    pageSize,
    jobs,
    dispatch,
  ]);

  useEffect(() => {
    let cancelled = false;

    const loadFirstPage = async () => {
      const companyId = companyPublicProfile?.id;

      if (!companyId) {
        if (profileLoading) {
          setInitialJobsLoading(true);
          return;
        }
        if (!cancelled) {
          setJobs([]);
          setCount(0);
          setTotalCount(0);
          setPage(1);
          setInitialJobsLoading(false);
          dispatch(companyJobsList([]));
        }
        return;
      }

      setInitialJobsLoading(true);
      setPage(1);

      try {
        const res: any = await companyPublicView.getCompanyJobs({
          companyId,
          pageSize,
          page: 1,
        });
        if (cancelled) return;

        const list =
          res?.flag === false
            ? []
            : Array.isArray(res?.data)
              ? res.data
              : [];

        setTotalCount(res?.pagination?.totalRecords ?? 0);
        setJobs(list);
        setCount(list.length);
        dispatch(companyJobsList(list));
        setPage(2);
      } catch {
        if (!cancelled) {
          setJobs([]);
          setCount(0);
          setTotalCount(0);
          dispatch(companyJobsList([]));
        }
      } finally {
        if (!cancelled) setInitialJobsLoading(false);
      }
    };

    loadFirstPage();

    return () => {
      cancelled = true;
    };
  }, [companyPublicProfile?.id, profileLoading, dispatch]);

  const isLoading = profileLoading || initialJobsLoading;

  if (isLoading) {
    return (
      <div>
        <div className="list-wrapper">
          {[1, 2, 3, 4].map((item) => (
            <Skeleton
              key={item}
              variant="rect"
              width={"100%"}
              height={195}
            />
          ))}
        </div>
      </div>
    );
  }

  if (jobs.length === 0) {
    return <NotFoundPage />;
  }

  return (
    <div>
      <div className="list-wrapper">
        {jobs.map((row) => (
          <React.Fragment
            key={_.get(row, "slug", "") || String(_.get(row, "id", ""))}
          >
            {jobsCard(row)}
          </React.Fragment>
        ))}
      </div>
      <div style={{ textAlign: "center" }}>
        <div>
          {btnLoading ? (
            <CircularProgress color="secondary" size={15} />
          ) : (
            <span
              className="span-link"
              hidden={count >= totalCount}
              onClick={() => {
                fetchMore();
              }}
            >
              SEE MORE
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default Jobs;
